/**
 * Book Analyzer Tutorial - Beginner (Introduction to Schemas)
 *
 * A beginner tutorial that introduces schemas (structured output).
 * The user creates a workflow with a user prompt for a book title,
 * then an agent that outputs a structured BookAnalysis schema.
 */
import { TutorialStep } from '../../types/tutorial';

const BOOK_ANALYZER_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Book Analyzer" workflow.';

/**
 * Book Analyzer Tutorial Steps
 *
 * A beginner tutorial that introduces schemas (structured output).
 * The user creates a workflow with a user prompt for a book title,
 * then an agent that outputs a structured BookAnalysis schema.
 *
 * Step Flow:
 * 1-3:   Navigate to Workflows, create workflow
 * 4-6:   Add User Prompt step
 * 7-10:  Add Book Analyzer Agent with structured output schema (created inline)
 * 11-13: Run, submit input, and view output
 */
export const BOOK_ANALYZER_TUTORIAL_STEPS: TutorialStep[] = [
  // --- Navigate to Workflows and create ---
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Welcome to Schemas!',
    description:
      "In this tutorial, you'll learn how to use __schemas__ to get __structured output__ from AI agents.\n\n" +
      'Instead of free-form text, a schema defines exactly what fields the agent returns - like a form template.\n\n' +
      "You'll build a __Book Analyzer__ that takes a book title and returns structured data about genre, characters, plot, and more.",
    actionPrompt: "Click 'Workflows' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Workflow',
    description: "Let's create a new workflow for our Book Analyzer.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      'Set up the workflow details:\n\n' +
      '1. For Name, enter: **Book Analyzer**\n\n' +
      '2. For Description, enter: **Analyzes a book and returns structured data about genre, characters, setting, plot, conflict, resolution, theme, and tone.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 2,
  },

  // --- Add User Prompt step ---
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add User Prompt',
    description:
      'Welcome to the workflow editor! First, we need a way for users to provide a book title.\n\n' +
      'Click the __+__ button below the __Start__ node to add a step.',
    actionPrompt: 'Click the __+__ button below the Start node.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      'Select the __User Prompt__ step type. This will ask the user for a book title.',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
    resumeFallbackStep: 4,
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      'Configure the User Prompt:\n\n' +
      '__Label__: **Get Book Title**\n\n' +
      "__Input Message__: **Enter the title of a book and I'll analyze it for you!**",
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
    resumeFallbackStep: 4,
  },

  // --- Add Book Analyzer Agent step (with inline agent + schema creation) ---
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Analyzer Agent',
    description:
      "Now we'll add the __Book Analyzer Agent__. This agent will use a __schema__ to return structured data instead of plain text.\n\n" +
      'A schema is like a blueprint that tells the agent exactly what fields to fill out.',
    actionPrompt: 'Click the __+__ button below the User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The __Book Analyzer Agent__ will __generate__ a structured analysis from the book title, so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create the Book Analyzer Agent',
    description:
      'First, create the agent that will analyze books.\n\n' +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Create from Scratch__\n' +
      '3. Name: **Book Analyzer Agent**\n' +
      "4. Instructions: **Analyze the given book title. Identify and describe the book's genre, main characters, setting, plot summary, central conflict, resolution, themes, and overall tone. Be detailed but concise.**\n\n" +
      "__Don't click Create Agent yet!__ First we'll add a schema in the next step.",
    actionPrompt:
      "Fill in the agent name and instructions, then click 'Continue'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Output Schema',
    description:
      'Now add a __schema__ so the agent returns structured data.\n\n' +
      '1. Scroll to __Response Formatting__ section\n' +
      '2. Change Output Type to __Structured Data (Schema)__\n' +
      '3. Click __Create New Schema__\n' +
      '4. Schema Name: **BookAnalysis**\n' +
      '5. Add properties: **genre** (str), **characters** (__List__ type, then select __String__ as element type), **setting** (str), **plot** (str), **conflict** (str), **resolution** (str), **theme** (str), **tone** (str)\n' +
      '6. Click __Create Schema__, then __Create Agent__\n\n' +
      'Finally, click __Add Step__ to add this step.',
    actionPrompt: "Create the schema, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
    resumeFallbackStep: 7,
  },

  // --- Run, submit input, and view output ---
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Run Your Workflow!',
    description:
      'Your Book Analyzer is ready! When you run it, the workflow will:\n\n' +
      '• Ask for a book title\n' +
      '• Send it to the Book Analyzer Agent\n' +
      '• Return structured data matching your BookAnalysis schema',
    actionPrompt: "Click 'Execute' to run your workflow!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Submit Your Input',
    description:
      'The workflow is waiting for your input! Type a book title in the text field and click **Submit**.\n\n' +
      'Try **To Kill a Mockingbird**, **1984**, or any book you like!',
    actionPrompt: "Enter a book title and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
  },
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View Your Analysis!',
    description:
      'Your workflow is running! Once complete, check out __View Final Output__ to see the structured analysis.\n\n' +
      'Notice how the output is organized into the exact fields you defined in the schema - genre, characters, setting, plot, conflict, resolution, theme, and tone.\n\n' +
      "__What you've learned:__\n" +
      '• Creating a __schema__ to define structured output\n' +
      '• Using __inline agent creation__ from the workflow editor\n' +
      '• How schemas turn free-form AI responses into __organized, predictable data__\n\n' +
      '__Tip:__ The BookAnalysis schema and the agent that uses it travel together. If you copy this ' +
      'workflow to another project via the __Duplicate__ icon on the Workflows page, the agent and ' +
      'its schema are auto-cloned into the destination so the copy works out of the box.\n\n' +
      "Click __Finish Tutorial__ when you're ready!",
    actionPrompt: "View the output, then click 'Finish Tutorial'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: BOOK_ANALYZER_ROUTE_MSG,
  },
];
